import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronRight } from 'lucide-react';
import { useTranslation } from '../../i18n';

import { Card } from '../../components/Shared/Card';
import { Button } from '../../components/Shared/Button';
import { FormField } from '../../components/Shared/FormField';
import { ContactPersonFields } from '../../components/Contacts/ContactPersonFields';
import {
  SnippetTextArea,
  type InvoiceSnippetContext,
} from '../../components/Shared/SnippetTextArea';
import SearchSelect from '../../components/Shared/SearchSelect';
import { useToastStore } from '../../store/useToastStore';
import { useInvoiceStore } from '../../store/useInvoiceStore';
import { useCustomerStore } from '../../store/useCustomerStore';
import { useObjectStore } from '../../store/useObjectStore';
import { useTextSnippetStore } from '../../store/useTextSnippetStore';
import {
  InvoicePositionEditor,
  createEmptyPositionRow,
  type PositionRow,
} from './components/InvoicePositionEditor';
import type { FacilityFlowCoreModulesInvoicesWorkflowsCreateManualInvoicePositionRequest } from '../../api/generated/models';
import type { Salutation } from '../../utils/salutation';

const NONE_OBJECT_ID = '__none__';

function pad2(n: number) {
  return n < 10 ? `0${n}` : `${n}`;
}

function firstOfMonth(year: number, month: number) {
  return `${year}-${pad2(month)}-01`;
}

function lastOfMonth(year: number, month: number) {
  const last = new Date(year, month, 0).getDate();
  return `${year}-${pad2(month)}-${pad2(last)}`;
}

export default function InvoiceManualCreatePage() {
  const { t } = useTranslation(['invoices', 'common']);
  const navigate = useNavigate();

  const { createManual, saving } = useInvoiceStore();
  const { customers, fetchCustomers } = useCustomerStore();
  const { objects, fetchAllObjects } = useObjectStore();
  const { addToast } = useToastStore();
  const fetchSnippets = useTextSnippetStore((s) => s.fetchSnippets);

  const now = new Date();
  const initialYear = now.getFullYear();
  const initialMonth = now.getMonth() + 1;

  const [customerId, setCustomerId] = useState<string>('');
  const [objectId, setObjectId] = useState<string>(NONE_OBJECT_ID);
  const [contactSalutation, setContactSalutation] = useState<Salutation | null>(null);
  const [contactPersonName, setContactPersonName] = useState('');
  const [year, setYear] = useState<number>(initialYear);
  const [month, setMonth] = useState<number>(initialMonth);
  const [serviceDateFrom, setServiceDateFrom] = useState<string>(
    firstOfMonth(initialYear, initialMonth),
  );
  const [serviceDateTo, setServiceDateTo] = useState<string>(
    lastOfMonth(initialYear, initialMonth),
  );
  const [positions, setPositions] = useState<PositionRow[]>([
    createEmptyPositionRow(),
  ]);
  const [introductionText, setIntroductionText] = useState('');
  const [closingText, setClosingText] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [buyerReference, setBuyerReference] = useState('');
  const [textsOpen, setTextsOpen] = useState(false);

  useEffect(() => {
    void fetchCustomers({ pageSize: 200 });
    void fetchAllObjects();
    void fetchSnippets({ isActive: true });
  }, [fetchCustomers, fetchAllObjects, fetchSnippets]);

  useEffect(() => {
    setServiceDateFrom(firstOfMonth(year, month));
    setServiceDateTo(lastOfMonth(year, month));
  }, [year, month]);

  const customerOptions = useMemo(
    () =>
      customers
        .filter((c) => c.id && c.name)
        .map((c) => ({ id: c.id as string, label: c.name as string })),
    [customers],
  );

  const objectOptions = useMemo(() => {
    const filtered = customerId
      ? objects.filter(
          (o) => (o as { customerId?: string }).customerId === customerId,
        )
      : [];
    const base = filtered
      .filter((o) => o.id && o.name)
      .map((o) => ({ id: o.id as string, label: o.name as string }));
    return [
      {
        id: NONE_OBJECT_ID,
        label: t('invoices:manualCreate.fields.objectNone'),
      },
      ...base,
    ];
  }, [objects, customerId, t]);

  const handleCustomerChange = (id: string) => {
    setCustomerId(id);
    setObjectId(NONE_OBJECT_ID);
  };

  const selectedObjectId = objectId !== NONE_OBJECT_ID ? objectId : null;

  // why: the object's responsible contact drives the auto-pick (FACF-102);
  // ContactPersonFields falls back to the customer's first contact without one.
  const preferredContactId = useMemo(
    () =>
      selectedObjectId
        ? objects.find((o) => o.id === selectedObjectId)?.responsibleContactId ?? null
        : null,
    [objects, selectedObjectId],
  );

  // why: preview context mirrors what CreateManualInvoiceWorkflow renders on save
  // (ADR-0020, Api repo) — invoice number, invoice date and due date do not exist yet, so
  // those tokens stay raw until the invoice is issued. taxRate is deliberately omitted: the
  // BE renders {TaxRate} from the invoice header rate (tenant default), not from a
  // position's VAT rate, so the preview leaves it raw rather than showing a differing value.
  const renderContext: InvoiceSnippetContext = useMemo(
    () => ({
      customerId: customerId || null,
      objectId: selectedObjectId,
      contactSalutation,
      contactPersonName: contactPersonName || null,
      serviceDateFrom: serviceDateFrom || null,
      serviceDateTo: serviceDateTo || null,
      billingPeriodYear: year,
      billingPeriodMonth: month,
      taxRate: null,
    }),
    [
      customerId,
      selectedObjectId,
      contactSalutation,
      contactPersonName,
      serviceDateFrom,
      serviceDateTo,
      year,
      month,
    ],
  );

  const validate = (): string | null => {
    if (!customerId) return t('invoices:manualCreate.errors.customerRequired');
    if (positions.length === 0)
      return t('invoices:manualCreate.errors.invalidPositions');
    if (
      positions.some(
        (p) => !p.title.trim() || p.quantity <= 0 || p.unitNetPrice < 0,
      )
    ) {
      return t('invoices:manualCreate.errors.invalidPositions');
    }
    if (serviceDateTo < serviceDateFrom) {
      return t('invoices:manualCreate.errors.dateRange');
    }
    return null;
  };

  const handleSubmit = async () => {
    const error = validate();
    if (error) {
      addToast(error, 'error');
      return;
    }

    const positionPayload: FacilityFlowCoreModulesInvoicesWorkflowsCreateManualInvoicePositionRequest[] = positions.map(
      (p) => ({
        title: p.title,
        description: p.description || undefined,
        // why: unit is required by CreateManualInvoicePositionRequest (non-nullable string).
        unit: p.unit || '',
        quantity: p.quantity,
        unitNetPrice: p.unitNetPrice,
        vatRate: p.vatRate,
        vatCategoryCode: 'S',
      }),
    );

    const id = await createManual({
      customerId,
      objectId: objectId !== NONE_OBJECT_ID ? objectId : null,
      year,
      month,
      serviceDateFrom,
      serviceDateTo,
      contactPersonName: contactPersonName || undefined,
      contactSalutation,
      introductionText: introductionText || undefined,
      closingText: closingText || undefined,
      paymentTerms: paymentTerms || undefined,
      buyerReference: buyerReference || undefined,
      positions: positionPayload,
    });

    if (id) {
      navigate(`/invoices/${id}`);
    }
  };

  const yearOptions = [initialYear + 1, initialYear, initialYear - 1];
  const monthKeys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => navigate('/invoices')}
            className="p-2"
            title={t('invoices:actions.backToList')}
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
              {t('invoices:manualCreate.title')}
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {t('invoices:manualCreate.subtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Customer & Object */}
      <Card>
        <h2 className="text-lg font-semibold mb-3">
          {t('invoices:manualCreate.sections.customer')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SearchSelect
            label={t('invoices:manualCreate.fields.customer')}
            required
            options={customerOptions}
            value={customerId}
            onChange={handleCustomerChange}
            placeholder={t('invoices:manualCreate.fields.customer')}
          />
          <SearchSelect
            label={t('invoices:manualCreate.fields.object')}
            options={objectOptions}
            value={objectId}
            onChange={setObjectId}
            placeholder={t('invoices:manualCreate.fields.object')}
          />
          <div className="md:col-span-2">
            <ContactPersonFields
              customerId={customerId}
              salutation={contactSalutation}
              name={contactPersonName}
              onChange={({ salutation, name }) => {
                setContactSalutation(salutation);
                setContactPersonName(name);
              }}
              preferredContactId={preferredContactId}
              nameLabel={t('invoices:manualCreate.fields.contactPersonName')}
            />
          </div>
        </div>
      </Card>

      {/* Period */}
      <Card>
        <h2 className="text-lg font-semibold mb-3">
          {t('invoices:manualCreate.sections.period')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('invoices:manualCreate.fields.year')}
              <span className="text-red-500 ml-1">*</span>
            </label>
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value, 10))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700"
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('invoices:manualCreate.fields.month')}
              <span className="text-red-500 ml-1">*</span>
            </label>
            <select
              value={month}
              onChange={(e) => setMonth(parseInt(e.target.value, 10))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700"
            >
              {monthKeys.map((m, i) => (
                <option key={m} value={i + 1}>
                  {t(`common:scheduleRule.months.${m}`)}
                </option>
              ))}
            </select>
          </div>
          <FormField
            label={t('invoices:manualCreate.fields.serviceDateFrom')}
            required
            type="date"
            value={serviceDateFrom}
            onChange={(e) => setServiceDateFrom(e.target.value)}
          />
          <FormField
            label={t('invoices:manualCreate.fields.serviceDateTo')}
            required
            type="date"
            value={serviceDateTo}
            onChange={(e) => setServiceDateTo(e.target.value)}
          />
        </div>
      </Card>

      {/* Positions */}
      <Card>
        <h2 className="text-lg font-semibold mb-3">
          {t('invoices:manualCreate.sections.positions')}
          <span className="text-red-500 ml-1">*</span>
        </h2>
        <InvoicePositionEditor
          positions={positions}
          onChange={setPositions}
          disabled={saving}
        />
      </Card>

      {/* Texts (collapsible) */}
      <Card>
        <button
          type="button"
          onClick={() => setTextsOpen((v) => !v)}
          className="flex items-center gap-2 text-lg font-semibold w-full text-left"
        >
          {textsOpen ? (
            <ChevronDown className="w-5 h-5" />
          ) : (
            <ChevronRight className="w-5 h-5" />
          )}
          {t('invoices:manualCreate.sections.texts')}
        </button>
        {textsOpen && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="md:col-span-2">
              <SnippetTextArea
                label={t('invoices:manualCreate.fields.introductionText')}
                name="introductionText"
                value={introductionText}
                onChange={setIntroductionText}
                category="InvoiceIntroduction"
                documentType="invoice"
                rows={3}
                renderContext={renderContext}
              />
            </div>
            <div className="md:col-span-2">
              <SnippetTextArea
                label={t('invoices:manualCreate.fields.closingText')}
                name="closingText"
                value={closingText}
                onChange={setClosingText}
                category="InvoiceClosingText"
                documentType="invoice"
                rows={3}
                renderContext={renderContext}
              />
            </div>
            <div className="md:col-span-2">
              <SnippetTextArea
                label={t('invoices:manualCreate.fields.paymentTerms')}
                name="paymentTerms"
                value={paymentTerms}
                onChange={setPaymentTerms}
                category="InvoicePaymentTerms"
                documentType="invoice"
                rows={2}
                renderContext={renderContext}
              />
            </div>
            <FormField
              label={t('invoices:manualCreate.fields.buyerReference')}
              value={buyerReference}
              onChange={(e) => setBuyerReference(e.target.value)}
            />
          </div>
        )}
      </Card>

      {/* Footer actions */}
      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" onClick={() => navigate('/invoices')} disabled={saving}>
          {t('invoices:manualCreate.actions.cancel')}
        </Button>
        <Button onClick={handleSubmit} disabled={saving}>
          {t('invoices:manualCreate.actions.submit')}
        </Button>
      </div>
    </div>
  );
}
